import { FC, Fragment, useCallback, useState } from 'react';
import { observer } from 'mobx-react';

import { BodyText, Button, Form, Icon, Label, Stack, Tooltip } from '@servicetitan/design-system';
import { InputFieldState } from '@servicetitan/form-state';

// eslint-disable-next-line no-restricted-globals
const clipboard = navigator.clipboard;

const webhookSignatureKeyHelp =
    'Key to sign webhook payloads. If provided, ServiceTitan will sign each payload with this key and include the signature in the X-Signature header. This allows your app to verify the payload’s authenticity.';

interface WebhookSignatureKeyEditableDisplayProps {
    fieldState: InputFieldState<string>;
    isReadonly: boolean;
    appDeleted: boolean;
    onDelete: () => void;
}

export const WebhookSignatureKeyEditableDisplay: FC<WebhookSignatureKeyEditableDisplayProps> =
    observer(({ fieldState, isReadonly, appDeleted, onDelete }) => {
        const [copyTooltipText, setCopyTooltipText] = useState('');

        const handleCopy = useCallback(async () => {
            try {
                await clipboard.writeText(fieldState.value);
                setCopyTooltipText('Copied!');
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error('Failed to copy.\n%O', error);
                setCopyTooltipText('Can’t copy');
            }
            setTimeout(() => {
                setCopyTooltipText('');
            }, 3000);
        }, [fieldState]);

        const handleGenerate = useCallback(() => {
            const bytes = new Uint8Array(16);
            // eslint-disable-next-line no-restricted-globals
            crypto.getRandomValues(bytes);
            const key = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
            fieldState.reset(key);
        }, [fieldState]);

        return isReadonly ? (
            <Stack direction="column" spacing={2}>
                <Label help={webhookSignatureKeyHelp}>
                    HMAC Key (optional but highly recommended)
                </Label>
                <Stack direction="row" spacing={2} alignItems="center">
                    <Icon name="vpn_key" size="18" />
                    <BodyText>{`${fieldState.value}\u2026`}</BodyText>
                    <Button negative outline onClick={onDelete} disabled={appDeleted}>
                        Delete
                    </Button>
                </Stack>
            </Stack>
        ) : (
            <Form.Input
                label="HMAC Key (optional but highly recommended)"
                labelProps={{ help: webhookSignatureKeyHelp }}
                shortLabel={<Icon name="vpn_key" />}
                shortLabelPosition="left"
                maxLength={64}
                minLength={32}
                value={fieldState.value}
                error={fieldState.error}
                onChange={fieldState.onChangeHandler}
                disabled={appDeleted}
                action={
                    <Fragment>
                        <Tooltip text={copyTooltipText} portal>
                            <Button
                                iconName="content_copy"
                                disabled={!fieldState.value || !!copyTooltipText}
                                onClick={handleCopy}
                            >
                                Copy
                            </Button>
                        </Tooltip>
                        <Button onClick={handleGenerate} disabled={appDeleted}>
                            Generate
                        </Button>
                    </Fragment>
                }
            />
        );
    });
